import { useEffect, useState } from 'react';

import { Divider, FormControl, Skeleton, Stack } from '@mui/material';
import LocationOnOutlinedIcon from '@mui/icons-material/LocationOnOutlined';
import LocalPhoneOutlinedIcon from '@mui/icons-material/LocalPhoneOutlined';
import PersonOutlineOutlinedIcon from '@mui/icons-material/PersonOutlineOutlined';
import { usePathname } from 'next/navigation';

import { useAppContext, useFacilityContext } from '@/context';
import { ASPEN_PATIENT_SERVICES_BASE_PATH } from '@/constants';
import { getReasonForVisit } from '@/utils/getReasonForVisit/getReasonForVisit';

import { ReasonForVisit } from './ReasonForVisit/ReasonForVisit';
import CustomButton from './UI/Button/CustomButton';
import CustomIconButton from './UI/Button/CustomIconButton';
import { headerActionsStyles } from './Header.styles';
import { getScheduleAppointmentUrl } from './getScheduleAppointmentUrl';

interface HeaderActionsProps {
	setShowLocationsDrawer: (open: boolean) => void;
	phoneNumber: string;
	onScheduleClick: (scheduleUrl: string, linkText: string) => void;
}

const HeaderActions = ({ setShowLocationsDrawer, phoneNumber, onScheduleClick }: HeaderActionsProps) => {
	const [origin, setOrigin] = useState('');
	const [reasonForVisit, setReasonForVisit] = useState('');

	const { config } = useAppContext();
	const { officeInfo: selectedOffice } = useFacilityContext();
	const pathname = usePathname();

	useEffect(() => {
		if (globalThis.window !== undefined) {
			setOrigin(globalThis.window.location.origin);
		}
	}, []);

	useEffect(() => {
		setReasonForVisit(getReasonForVisit(pathname));
	}, [pathname]);

	const handleLocationClick = () => {
		setShowLocationsDrawer(true);
	};

	const scheduleAppointmentUrl = getScheduleAppointmentUrl({
		config,
		origin,
		selectedOffice,
		getReasonForVisit: () => reasonForVisit,
	});

	return (
		<>
			{/* Desktop actions */}
			<Stack direction="row" sx={headerActionsStyles.desktopActions} id="header-actions-desktop">
				<Stack direction="row" sx={headerActionsStyles.actionGroup}>
					{/* Reason for visit */}
					<FormControl sx={headerActionsStyles.reasonSelectWrapper}>
						<ReasonForVisit
							reasonForVisit={reasonForVisit}
							handleReasonForVisitChange={(event) => setReasonForVisit(event.target.value)}
							id="reason-for-visit-select-header"
						/>
					</FormControl>

					<Divider orientation="vertical" flexItem sx={headerActionsStyles.verticalDivider} />

					{/* Location */}
					{selectedOffice ? (
						<CustomButton
							variantType="tertiary"
							size="small"
							startIcon={<LocationOnOutlinedIcon sx={{ '&.MuiSvgIcon-root': { fontSize: '1.5rem' } }} />}
							onClick={handleLocationClick}
							sx={headerActionsStyles.officeNameButton}
						>
							{selectedOffice.name}
						</CustomButton>
					) : (
						<Skeleton variant="rounded" width={120} height={32} />
					)}

					{/* Schedule */}
					<CustomButton
						variantType="primaryDefault"
						size="small"
						href={scheduleAppointmentUrl}
						onClick={() => onScheduleClick(scheduleAppointmentUrl, 'Schedule appointment')}
					>
						Schedule
					</CustomButton>
				</Stack>

				{/* Phone (desktop) */}
				<CustomButton
					variantType="secondary"
					size="small"
					startIcon={<LocalPhoneOutlinedIcon sx={{ '&.MuiSvgIcon-root': { fontSize: '1.5rem' } }} />}
					href={`tel:${phoneNumber}`}
					title={`tel:${phoneNumber}`}
					sx={headerActionsStyles.phoneButtonDesktop}
					className="InvocaNumber"
				>
					{phoneNumber}
				</CustomButton>
			</Stack>

			{/* User / Patient portal */}
			<Stack sx={headerActionsStyles.userIconWrapper}>
				<CustomIconButton
					size="small"
					href={origin ? origin + ASPEN_PATIENT_SERVICES_BASE_PATH : ASPEN_PATIENT_SERVICES_BASE_PATH}
					aria-label="Patient Portal"
					title="Patient Portal"
				>
					<PersonOutlineOutlinedIcon />
				</CustomIconButton>
			</Stack>
		</>
	);
};

export default HeaderActions;
